/* VRS Rankings — Valve Regional Standings (ValveSoftware/counter-strike_regional_standings), proxied and
 * cached for 1 h by server/routes/vrs.js. Region / year / snapshot live in the URL. Movement (▲/▼/NEW)
 * compares with the previous snapshot of the same region (second request, same server cache). */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './vrs/vrs.css';

const REPO = 'https://github.com/ValveSoftware/counter-strike_regional_standings';
const REGIONS = [
  { key: 'global', label: 'Global' },
  { key: 'europe', label: 'Europe' },
  { key: 'americas', label: 'Americas' },
  { key: 'asia', label: 'Asia' },
];
const YEARS = [2026, 2025, 2024];
const PER_PAGE = 50;

const sourceUrl = (year, region, date) => `${REPO}/blob/main/live/${year}/standings_${region}_${date}.md`;

// "2026_04_06" → localized "6 Apr 2026"
function formatDate(d, lng) {
  if (!d) return '';
  const [y, m, day] = d.split('_').map(Number);
  try { return new Date(Date.UTC(y, m - 1, day)).toLocaleDateString(lng || undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }); }
  catch (_) { return d.replace(/_/g, '-'); }
}

const Ic = ({ children, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const GlobeIc = () => <Ic><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></Ic>;
const ExtIc = () => <Ic size={12}><path d="M14 3h7v7" /><path d="M10 14L21 3" /><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" /></Ic>;
const GitIc = () => <Ic size={13}><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" /></Ic>;
const SearchIc = () => <Ic size={13}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></Ic>;

export default function VRSRankings() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const region = REGIONS.some(r => r.key === params.get('region')) ? params.get('region') : 'global';
  const year = YEARS.includes(Number(params.get('year'))) ? Number(params.get('year')) : YEARS[0];
  const dateParam = params.get('date') || '';
  const setParam = (patch) => setParams(prev => {
    const n = new URLSearchParams(prev);
    Object.entries(patch).forEach(([k, v]) => (v == null || v === '' ? n.delete(k) : n.set(k, String(v))));
    return n;
  }, { replace: true });

  const [regionDates, setRegionDates] = useState(null); // { global: [...], europe: [...] } newest first
  const [teams, setTeams] = useState([]);
  const [prevRanks, setPrevRanks] = useState(null); // Map team → rank in the previous snapshot
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // snapshot dates of this year (per region — not every region has every date)
  useEffect(() => {
    let alive = true;
    setRegionDates(null); setError('');
    axios.get(`/api/vrs/dates/${year}`).then(res => {
      if (!alive) return;
      const rd = {};
      Object.entries(res.data.regionDates || {}).forEach(([r, list]) => { rd[r] = [...list].sort().reverse(); });
      setRegionDates(rd);
    }).catch(() => { if (alive) { setRegionDates({}); setError(t('vrsPage.datesFailed', 'Could not load the snapshot list from GitHub.')); } });
    return () => { alive = false; };
  }, [year, t]);

  const dates = useMemo(() => (regionDates && regionDates[region]) || [], [regionDates, region]);
  const date = dates.includes(dateParam) ? dateParam : (dates[0] || '');
  const prevDate = date ? dates[dates.indexOf(date) + 1] : null; // older snapshot of the same year

  // rankings of the snapshot (+ the previous one for movement)
  useEffect(() => {
    if (!regionDates) return undefined;
    if (!date) { setTeams([]); setLoading(false); return undefined; }
    let alive = true;
    setLoading(true); setError(''); setPage(1);
    axios.get(`/api/vrs/rankings/${year}/${region}/${date}`).then(res => {
      if (alive) setTeams(res.data.teams || []);
    }).catch(() => {
      if (alive) { setTeams([]); setError(t('vrsPage.rankingsFailed', 'Could not load the standings from GitHub. Try again in a minute.')); }
    }).finally(() => { if (alive) setLoading(false); });
    setPrevRanks(null);
    if (prevDate) {
      axios.get(`/api/vrs/rankings/${year}/${region}/${prevDate}`).then(res => {
        if (alive) setPrevRanks(new Map((res.data.teams || []).map(x => [x.team.toLowerCase(), x.rank])));
      }).catch(() => {});
    }
    return () => { alive = false; };
  }, [regionDates, year, region, date, prevDate, t]);

  useEffect(() => { setPage(1); }, [search]);

  const q = search.trim().toLowerCase();
  const filtered = q ? teams.filter(x => x.team.toLowerCase().includes(q) || x.roster.some(p => p.toLowerCase().includes(q))) : teams;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const rows = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const topPoints = teams[0]?.points || 1;

  const movement = (team) => {
    if (!prevRanks) return null;
    const before = prevRanks.get(team.team.toLowerCase());
    if (before == null) return { kind: 'new' };
    const d = before - team.rank;
    return d > 0 ? { kind: 'up', d } : d < 0 ? { kind: 'down', d: -d } : { kind: 'same' };
  };
  const Move = ({ team }) => {
    const m = movement(team);
    if (!m) return <span className="vr-move none">–</span>;
    if (m.kind === 'new') return <span className="vr-move new">{t('vrsPage.new', 'New')}</span>;
    if (m.kind === 'same') return <span className="vr-move same">=</span>;
    return <span className={`vr-move ${m.kind}`}>{m.kind === 'up' ? '▲' : '▼'}{m.d}</span>;
  };
  const hl = (text) => {
    if (!q) return text;
    const i = text.toLowerCase().indexOf(q);
    return i < 0 ? text : <>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
  };

  const regionLabel = (key) => t(`vrs.region.${key}`, REGIONS.find(r => r.key === key)?.label || key);

  return (
    <div className="vr-page">
      <div className="vr-head">
        <div className="vr-head-text">
          <h1 className="vr-title">{t('nav.vrsRankings', 'VRS Rankings')}</h1>
          <p className="vr-subtitle">{t('vrsPage.subtitle', 'Valve Regional Standings - the official ranking Valve uses for Major and event invites.')}</p>
        </div>
        <div className="vr-sources">
          {date && (
            <a className="vr-btn" href={sourceUrl(year, region, date)} target="_blank" rel="noopener noreferrer" title={`standings_${region}_${date}.md`}>
              <ExtIc /> {t('vrsPage.viewSource', 'Source file')}
            </a>
          )}
          <a className="vr-btn" href={REPO} target="_blank" rel="noopener noreferrer">
            <GitIc /> {t('vrsPage.repo', 'Valve on GitHub')}
          </a>
        </div>
      </div>

      <div className="vr-toolbar">
        <div className="vr-regions" role="tablist">
          {REGIONS.map(r => (
            <button key={r.key} type="button" role="tab" aria-selected={region === r.key} className={`vr-region${region === r.key ? ' on' : ''}`} onClick={() => setParam({ region: r.key, date: null })}>
              {r.key === 'global' && <GlobeIc />}{regionLabel(r.key)}
            </button>
          ))}
        </div>
        <div className="vr-pickers">
          <select className="vr-select" value={year} onChange={e => setParam({ year: e.target.value, date: null })} aria-label={t('vrsPage.year', 'Year')}>
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className="vr-select" value={date} onChange={e => setParam({ date: e.target.value })} disabled={!dates.length} aria-label={t('vrsPage.snapshot', 'Snapshot')}>
            {!dates.length && <option value="">{regionDates ? t('vrsPage.noSnapshots', 'No snapshots') : t('vrs.loading', 'Loading…')}</option>}
            {dates.map((d, i) => <option key={d} value={d}>{formatDate(d, i18n.language)}{i === 0 ? ` (${t('vrsPage.latest', 'latest')})` : ''}</option>)}
          </select>
          <label className="vr-search">
            <SearchIc />
            <input value={search} onChange={e => setSearch(e.target.value)} aria-label={t('vrs.searchPlaceholder', 'Search team or player...')} placeholder={t('vrs.searchPlaceholder', 'Search team or player...')} />
            {search && <button type="button" onClick={() => setSearch('')} aria-label={t('common.clear', 'Clear')}>×</button>}
          </label>
        </div>
      </div>

      <div className="vr-meta">
        <span><b>{teams.length}</b> {t('vrs.teams', 'Teams')}</span>
        {date && <span>{t('vrsPage.snapshotOf', 'Snapshot {{date}}', { date: formatDate(date, i18n.language) })}</span>}
        {prevDate && <span>{t('vrsPage.movementVs', 'Movement vs {{date}}', { date: formatDate(prevDate, i18n.language) })}</span>}
        {q && <span>{t('vrsPage.matches', 'Matches: {{count}}', { count: filtered.length })}</span>}
      </div>

      {error && <div className="vr-error">{error}</div>}

      <div className="vr-table">
        <div className="vr-row vr-row-head">
          <span className="c-rank">#</span>
          <span className="c-move">{t('vrsPage.change', 'Change')}</span>
          <span className="c-team">{t('vrs.team', 'Team')}</span>
          <span className="c-points">{t('vrs.vrsPoints', 'VRS Points')}</span>
          <span className="c-roster">{t('vrs.roster', 'Roster')}</span>
        </div>
        {loading ? (
          Array.from({ length: 8 }).map((_, i) => <div key={i} className="vr-row skel"><span /></div>)
        ) : rows.length === 0 ? (
          <div className="vr-empty">{q ? t('vrs.noMatch', 'No teams match your search') : t('vrs.noData', 'No data available')}</div>
        ) : rows.map(team => (
          <div key={`${team.rank}-${team.team}`} className={`vr-row${team.rank <= 3 ? ` top top${team.rank}` : ''}`}>
            <span className="c-rank"><span className="vr-rank">{team.rank}</span></span>
            <span className="c-move"><Move team={team} /></span>
            <span className="c-team">{hl(team.team)}</span>
            <span className="c-points">
              <b>{team.points.toLocaleString(i18n.language)}</b>
              <span className="vr-bar"><span style={{ width: `${Math.max(2, Math.round((team.points / topPoints) * 100))}%` }} /></span>
            </span>
            <span className="c-roster">
              {team.roster.map(p => <span key={p} className="vr-player">{hl(p)}</span>)}
            </span>
          </div>
        ))}
        {totalPages > 1 && !loading && (
          <div className="vr-pager">
            <button type="button" className="vr-btn small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ {t('seasonMatches.prev', 'Prev')}</button>
            <span>{t('seasonMatches.pageOf', 'Page {{page}} of {{total}}', { page, total: totalPages })}</span>
            <button type="button" className="vr-btn small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>{t('common.next', 'Next')} ›</button>
          </div>
        )}
      </div>

      <p className="vr-foot">
        {t('vrsPage.footer', 'Data: Valve Regional Standings, published by Valve on GitHub. Valve releases a new snapshot once a month (first Monday); the panel caches it for one hour.')}{' '}
        <a href={REPO} target="_blank" rel="noopener noreferrer">{REPO.replace('https://', '')}</a>
      </p>
    </div>
  );
}
